import { FC, useCallback, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';

import { Checkbox } from 'common/components';

import {
    FILTER,
    NO_RECORDS_FOUND,
    SEARCH_CATEGORY,
    SEARCH_DEBOUNCE_DELAY,
    SEARCH_INDICATORS,
    SEARCH_RESULTS,
    SEARCH_SUB_CATEGORY
} from 'common/config/search.config';
import FilterList from '../filter-list/FilterList';
import { RootState } from 'common/redux/core/root.reducer';
import {
    getSearchList,
    getSearchListPagination,
    getSearchListStatus
} from 'modules/master-data/redux/masterData.selector';
import { ApiStatus, EntityActions, EntityType } from 'common/enums';
import { SearchDTO } from 'common/models';
import useDebounce from 'common/hooks/useDebounce';
import { masterDataActions } from 'modules/master-data/redux';
import InfiniteScroll from 'common/components/custom/infinite-scroll/InfiniteScroll';
import { UseMasterDataAccess } from 'modules/master-data/role-access-hooks/useMasterDataAccess';

type SearchResultListProps = {
    searchVal: string;
    isUploadDisabled: boolean;
    uploadedUser: string;
    isSyncInProgress: boolean;
    masterDataFeatureActionList: UseMasterDataAccess;
    handleAction: (action: EntityActions, data: any) => void;
    handleDetailsClick: (data: any) => void;
};

const SearchResultList: FC<SearchResultListProps> = ({
    searchVal,
    isUploadDisabled,
    uploadedUser,
    isSyncInProgress,
    masterDataFeatureActionList,
    handleAction,
    handleDetailsClick
}) => {
    const dispatch = useDispatch();

    // States
    const [categoriesCheckbox, setCategoriesCheckbox] = useState(true);
    const [subCategoriesCheckbox, setSubCategoriesCheckbox] = useState(true);
    const [indicatorsCheckbox, setIndicatorsCheckbox] = useState(true);
    const [pageNumber, setPageNumber] = useState<number>(1);

    // Selectors
    const searchList = useSelector((state: RootState) => getSearchList(state));
    const searchListStatus = useSelector((state: RootState) => getSearchListStatus(state));
    const searchListPagination = useSelector((state: RootState) => getSearchListPagination(state));

    const getSearchApiPayload = useCallback(() => {
        const filter: string[] = [];
        categoriesCheckbox && filter.push(EntityType.Category);
        subCategoriesCheckbox && filter.push(EntityType.SubCategory);
        indicatorsCheckbox && filter.push(EntityType.Indicator);
        const apiPayload: SearchDTO = {
            searchText: searchVal,
            filter: filter,
            pageNumber: 1,
            pageSize: 20,
            loadPath: true
        };

        return apiPayload;
    }, [categoriesCheckbox, indicatorsCheckbox, searchVal, subCategoriesCheckbox]);

    useDebounce(
        () => {
            if (searchVal.length >= 3)
                dispatch(masterDataActions.searchList(getSearchApiPayload()));
            // TODO: use ref
            setTimeout(function () {
                document.getElementById('filterList')?.scrollTo({
                    top: 0,
                    left: 0,
                    behavior: 'smooth'
                });
            }, 0);
        },
        [searchVal, categoriesCheckbox, subCategoriesCheckbox, indicatorsCheckbox],
        SEARCH_DEBOUNCE_DELAY
    );

    const fetchNextSearchList = () => {
        if (
            searchListPagination &&
            searchListPagination.totalPages > searchListPagination.currentPage
        ) {
            const apiPayload = getSearchApiPayload();
            apiPayload.pageNumber = pageNumber + 1;
            dispatch(masterDataActions.searchList(apiPayload));
            setPageNumber(pageNumber + 1);
        }
    };

    return (
        <>
            <span className="search-results">{SEARCH_RESULTS}</span>
            <div className="search-filter-container">
                {FILTER} &nbsp;&nbsp;
                <Checkbox
                    id="categories"
                    value="categories"
                    name="categories"
                    className="search-checkbox"
                    checked={categoriesCheckbox}
                    onChange={() => {
                        setCategoriesCheckbox(!categoriesCheckbox);
                    }}>
                    {SEARCH_CATEGORY}
                </Checkbox>
                <Checkbox
                    id="sub-categories"
                    value="sub-categories"
                    name="sub-categories"
                    className="search-checkbox"
                    checked={subCategoriesCheckbox}
                    onChange={() => {
                        setSubCategoriesCheckbox(!subCategoriesCheckbox);
                    }}>
                    {SEARCH_SUB_CATEGORY}
                </Checkbox>
                <Checkbox
                    id="indicators"
                    value="indicators"
                    name="indicators"
                    className="search-checkbox"
                    checked={indicatorsCheckbox}
                    onChange={() => {
                        setIndicatorsCheckbox(!indicatorsCheckbox);
                    }}>
                    {SEARCH_INDICATORS}
                </Checkbox>
            </div>
            {searchList.length === 0 ? (
                <center>
                    <b className="search-results">{NO_RECORDS_FOUND}</b>
                </center>
            ) : (
                <div className="filter-list-wrapper" id="filterList">
                    {searchList.map((list: any, index: number) => {
                        return (
                            <FilterList
                                key={index}
                                searchVal={searchVal}
                                data={list}
                                isUploadDisabled={isUploadDisabled}
                                uploadedUser={uploadedUser}
                                isSyncInProgress={isSyncInProgress}
                                masterDataFeatureActionList={masterDataFeatureActionList}
                                onAction={handleAction}
                                handleDetailsClick={handleDetailsClick}
                            />
                        );
                    })}
                    <InfiniteScroll
                        targetId="filterList"
                        isDataLoading={searchListStatus === ApiStatus.LOADING}
                        currentRecordCount={
                            searchListPagination &&
                            searchListPagination.currentPage * searchListPagination.pageSize
                        }
                        totalRecordCount={
                            searchListPagination ? searchListPagination.totalCount : 0
                        }
                        fetchNext={fetchNextSearchList}
                    />
                </div>
            )}
        </>
    );
};

export default SearchResultList;
